import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { 
  Paperclip, Upload, X, File, FileText, Image, Download, 
  Loader2, AlertCircle, Eye, ChevronDown, ChevronRight
} from 'lucide-react';
import api, { formatErrorMessage } from '../utils/api';
import { toast } from 'sonner';

const TaskAttachments = ({ 
  entityType = 'task', 
  entityId, 
  attachments: propAttachments = [], 
  onAttachmentsChange,
  canUpload = true,
  compact = false,
  showBitacoraAttachments = false  // Mostrar adjuntos de la bitácora
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState(propAttachments);
  const [bitacoraAttachments, setBitacoraAttachments] = useState([]);
  const [expandedBitacora, setExpandedBitacora] = useState({});
  const fileInputRef = useRef(null);

  // Si showBitacoraAttachments está activado, cargar todos los adjuntos
  useEffect(() => {
    if (showBitacoraAttachments && entityId && entityType === 'task') {
      loadAllAttachments();
    }
  }, [entityId, showBitacoraAttachments]);

  // Actualizar attachments cuando cambian las props
  useEffect(() => {
    setAttachments(propAttachments);
  }, [propAttachments]);

  const loadAllAttachments = async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      const response = await api.get(`/tasks/${entityId}/all-attachments`);
      if (response.data.direct_attachments) {
        setAttachments(response.data.direct_attachments);
      }
      if (response.data.bitacora_attachments) {
        setBitacoraAttachments(response.data.bitacora_attachments);
        // Auto-expandir todas las secciones de bitácora si hay 3 o menos
        if (response.data.bitacora_attachments.length <= 3) {
          const autoExpand = {};
          response.data.bitacora_attachments.forEach((_, idx) => {
            autoExpand[idx] = true;
          });
          setExpandedBitacora(autoExpand);
        }
      }
    } catch (error) {
      // Si no tiene permiso, simplemente no mostramos los adjuntos de bitácora
      console.log('Could not load all attachments:', error.response?.status);
    } finally {
      setLoading(false);
    }
  };

  const getFileIcon = (contentType) => {
    if (contentType?.startsWith('image/')) return Image;
    if (contentType?.includes('pdf')) return FileText;
    return File;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      const uploadedFiles = [];
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append('file', file);
        formData.append('entity_type', entityType);
        formData.append('entity_id', entityId);

        const response = await api.post('/files/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              ((i + progressEvent.loaded / progressEvent.total) / files.length) * 100
            );
            setUploadProgress(percentCompleted);
          }
        });

        uploadedFiles.push(response.data);
      }

      toast.success(`${files.length} archivo(s) subido(s) correctamente`);
      
      // Notificar al componente padre
      if (onAttachmentsChange) {
        onAttachmentsChange([...attachments, ...uploadedFiles]);
      }
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setUploading(false);
      setUploadProgress(0);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveAttachment = async (attachmentId) => {
    try {
      await api.delete(`/files/${attachmentId}`);
      toast.success('Archivo eliminado');
      
      if (onAttachmentsChange) {
        onAttachmentsChange(attachments.filter(a => a.id !== attachmentId));
      }
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const handleDownload = async (attachment) => {
    try {
      // Usar la URL directa del archivo si está disponible
      const fileUrl = attachment.url || `/api/files/${attachment.id}`;
      
      const response = await api.get(`/files/${attachment.id}`, {
        responseType: 'blob'
      });
      
      // Usar el content type de la respuesta si está disponible, o de los metadatos
      const contentType = response.headers?.['content-type'] || attachment.content_type || 'application/octet-stream';
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.original_name || 'archivo';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      // Fallback: abrir URL directamente en nueva pestaña
      try {
        if (attachment.url) {
          window.open(attachment.url, '_blank');
        } else {
          window.open(`/api/files/${attachment.id}`, '_blank');
        }
      } catch (fallbackError) {
        toast.error('Error al descargar archivo');
      }
    }
  };

  const handlePreview = (attachment) => {
    if (attachment.url) {
      window.open(attachment.url, '_blank');
    }
  };

  // Vista compacta para mostrar en cards de tareas
  if (compact) {
    if (attachments.length === 0) return null;
    
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Paperclip className="w-4 h-4" />
        <span>{attachments.length} adjunto{attachments.length !== 1 ? 's' : ''}</span>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header con botón de upload */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
          <Paperclip className="w-4 h-4" />
          <span>Archivos Adjuntos ({attachments.length})</span>
        </div>
        
        {canUpload && (
          <div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              accept="*/*"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || !entityId}
              data-testid="upload-attachment-btn"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  {uploadProgress}%
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-1" />
                  Adjuntar
                </>
              )}
            </Button>
          </div>
        )}
      </div>

      {/* Mensaje si no hay entityId */}
      {!entityId && canUpload && (
        <div className="flex items-center gap-2 p-3 bg-amber-50 text-amber-700 rounded-lg text-sm">
          <AlertCircle className="w-4 h-4" />
          <span>Guarda la tarea primero para poder adjuntar archivos</span>
        </div>
      )}

      {/* Lista de archivos */}
      {attachments.length > 0 ? (
        <div className="space-y-2">
          {attachments.map((attachment) => {
            const FileIcon = getFileIcon(attachment.content_type);
            
            return (
              <div 
                key={attachment.id} 
                className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200 group"
              >
                <div className="p-2 rounded bg-slate-200">
                  <FileIcon className="w-4 h-4 text-slate-600" />
                </div>
                
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">
                    {attachment.original_name}
                  </p>
                  <p className="text-xs text-slate-500">
                    {formatFileSize(attachment.size)}
                    {attachment.uploaded_by && ` • ${attachment.uploaded_by}`}
                  </p>
                </div>
                
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {attachment.content_type?.startsWith('image/') && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handlePreview(attachment)}
                      className="h-8 w-8"
                      title="Ver"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDownload(attachment)}
                    className="h-8 w-8"
                    title="Descargar"
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                  {canUpload && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveAttachment(attachment.id)}
                      className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                      title="Eliminar"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-6 text-slate-400 text-sm">
          <Paperclip className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>No hay archivos adjuntos</p>
        </div>
      )}

      {/* Progress bar durante upload */}
      {uploading && (
        <div className="w-full bg-slate-200 rounded-full h-2">
          <div 
            className="bg-primary h-2 rounded-full transition-all duration-300"
            style={{ width: `${uploadProgress}%` }}
          />
        </div>
      )}

      {/* Adjuntos de la Bitácora */}
      {showBitacoraAttachments && bitacoraAttachments.length > 0 && (
        <div className="mt-4 border-t border-slate-200 pt-4">
          <p className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
            <File className="w-4 h-4" />
            Historial de Documentos ({bitacoraAttachments.reduce((acc, b) => acc + b.files.length, 0)})
          </p>
          <div className="space-y-2">
            {bitacoraAttachments.map((bitacora, idx) => (
              <div key={idx} className="border border-slate-200 rounded-lg overflow-hidden">
                <button
                  type="button"
                  className="w-full p-3 bg-slate-50 flex items-center justify-between text-left hover:bg-slate-100 transition-colors"
                  onClick={() => setExpandedBitacora(prev => ({ ...prev, [idx]: !prev[idx] }))}
                >
                  <div className="flex items-center gap-2">
                    {expandedBitacora[idx] ? (
                      <ChevronDown className="w-4 h-4 text-slate-500" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-slate-500" />
                    )}
                    <span className="text-sm font-medium text-slate-700">{bitacora.usuario}</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      bitacora.tipo === 'asignacion' ? 'bg-blue-100 text-blue-700' :
                      bitacora.tipo === 'completada' ? 'bg-green-100 text-green-700' :
                      bitacora.tipo === 'aprobacion' ? 'bg-emerald-100 text-emerald-700' :
                      bitacora.tipo === 'rechazo' ? 'bg-red-100 text-red-700' :
                      'bg-slate-100 text-slate-700'
                    }`}>
                      {bitacora.tipo}
                    </span>
                  </div>
                  <span className="text-xs text-slate-500 flex items-center gap-1">
                    <Paperclip className="w-3 h-3" />
                    {bitacora.files.length} archivo(s)
                  </span>
                </button>
                
                {expandedBitacora[idx] && (
                  <div className="p-3 space-y-2 bg-white">
                    <p className="text-xs text-slate-500 mb-2">
                      {bitacora.descripcion}
                    </p>
                    {bitacora.files.map((file) => {
                      const FileIcon = getFileIcon(file.content_type);
                      return (
                        <div 
                          key={file.id} 
                          className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg group"
                        >
                          <div className="p-2 rounded bg-slate-200">
                            <FileIcon className="w-4 h-4 text-slate-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-800 truncate">
                              {file.original_name}
                            </p>
                            <p className="text-xs text-slate-500">
                              {formatFileSize(file.size)}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownload(file)}
                            className="opacity-0 group-hover:opacity-100"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Loading indicator */}
      {loading && (
        <div className="flex items-center justify-center p-4">
          <Loader2 className="w-5 h-5 animate-spin text-primary" />
          <span className="ml-2 text-sm text-slate-500">Cargando documentos...</span>
        </div>
      )}
    </div>
  );
};

export default TaskAttachments;
